import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import api from '../api/client.js';
import { useAuth } from '../auth/AuthContext.jsx';
import clsx from 'clsx';

const BRANCHES = [
  { id: 1, name: 'Lathrop' },
  { id: 2, name: 'Fresno' },
  { id: 3, name: 'Fontana' },
  { id: 4, name: 'Delhi' },
  { id: 5, name: 'Troutdale' },
];

const money = (v) =>
  v == null ? '—' : Number(v).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });

function today() {
  return new Date().toISOString().slice(0, 10);
}

const TOUCH_LABELS = {
  call: 'Call',
  visit: 'Visit',
  email: 'Email',
  text: 'Text',
  other: 'Other',
};

const TABS = [
  { id: 'summary',  label: 'Summary' },
  { id: 'pipeline', label: 'Pipeline' },
];

export default function EodParts() {
  const [tab, setTab] = useState('summary');

  return (
    <div className="space-y-4">
      <header>
        <div className="section-eyebrow">Parts</div>
        <h1 className="text-2xl">End-of-Day Summary</h1>
        <div className="rule-crimson" />
      </header>

      <div className="border-b border-panelDark flex gap-1">
        {TABS.map(t => (
          <button key={t.id}
            onClick={() => setTab(t.id)}
            className={clsx(
              'px-4 py-2 text-sm border-b-2 -mb-px',
              tab === t.id ? 'border-crimson text-crimson font-medium' : 'border-transparent text-navy hover:text-crimson'
            )}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'summary'  && <SummaryTab />}
      {tab === 'pipeline' && <PipelineTab />}
    </div>
  );
}

function SummaryTab() {
  const { user } = useAuth();
  const [branchId, setBranchId] = useState(2);
  const [date, setDate] = useState(today);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['reports', 'eod', branchId, date],
    queryFn: () =>
      api.get('/reports/eod', { params: { branch_id: branchId, date } }).then(r => r.data),
  });

  const branchName = BRANCHES.find(b => b.id === branchId)?.name || `Branch ${branchId}`;

  return (
    <div className="space-y-4">
      <div className="card p-3 flex flex-wrap gap-3 items-end">
        <div>
          <label className="label">Branch</label>
          <select className="input w-auto" value={branchId}
                  onChange={e => setBranchId(parseInt(e.target.value, 10))}>
            {BRANCHES.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Date</label>
          <input className="input" type="date" value={date} max={today()}
                 onChange={e => setDate(e.target.value)} />
        </div>
        <button className="btn-ghost text-sm" onClick={() => setDate(today())}>Today</button>
      </div>

      {isLoading && <div className="card p-4 text-muted">Loading…</div>}
      {isError  && <div className="card p-4 text-crimson">Failed to load EOD data.</div>}

      {data && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <Stat label="SOs Invoiced" value={data.so_count} />
            <Stat label="Parts" value={money(data.total_parts)} />
            <Stat label="Labor" value={money(data.total_labor)} />
            <Stat label="Total" value={money(data.total_subtotal)} highlight />
          </div>

          <section>
            <h2 className="text-base font-semibold text-navy mb-2">
              Service Orders — {branchName} · {date}
            </h2>
            {data.service_orders.length === 0 ? (
              <div className="card p-4 text-muted text-sm">No SOs invoiced today.</div>
            ) : (
              <div className="card p-0 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-wider text-muted border-b border-navy-100">
                      <th className="px-3 py-2">SO #</th>
                      <th className="px-3 py-2">Customer</th>
                      <th className="px-3 py-2">VIN</th>
                      <th className="px-3 py-2 text-right">Parts</th>
                      <th className="px-3 py-2 text-right">Labor</th>
                      <th className="px-3 py-2 text-right">Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.service_orders.map(so => (
                      <tr key={so.id} className="border-b border-navy-100/60 hover:bg-panel">
                        <td className="px-3 py-2 font-mono text-xs">{so.sls_id || so.id}</td>
                        <td className="px-3 py-2">{so.customer_name || so.cus_name || '—'}</td>
                        <td className="px-3 py-2 font-mono text-xs text-muted">{so.vin || '—'}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{money(so.amt_parts)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{money(so.amt_labor)}</td>
                        <td className="px-3 py-2 text-right tabular-nums font-medium">{money(so.amt_subtotal)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-panel text-sm font-semibold">
                      <td className="px-3 py-2" colSpan={3}>Total ({data.so_count} SOs)</td>
                      <td className="px-3 py-2 text-right tabular-nums">{money(data.total_parts)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{money(data.total_labor)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{money(data.total_subtotal)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </section>

          <section>
            <h2 className="text-base font-semibold text-navy mb-2">
              Notes Created Today ({data.notes.length})
            </h2>
            {data.notes.length === 0 ? (
              <div className="card p-4 text-muted text-sm">No notes created today.</div>
            ) : (
              <div className="space-y-2">
                {data.notes.map(n => (
                  <div key={n.id} className="card p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="text-sm font-medium">{n.customer_name || 'General'}</div>
                        {n.summary && <div className="text-sm text-navy mt-0.5">{n.summary}</div>}
                        <div className="text-xs text-muted mt-1">
                          {TOUCH_LABELS[n.touch_type] || n.touch_type}
                          {n.user_name && ` · ${n.user_name}`}
                          {' · '}
                          {new Date(n.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

function PipelineTab() {
  const q = useQuery({
    queryKey: ['parts', 'pipeline'],
    queryFn: () => api.get('/parts/pipeline').then(r => r.data),
  });
  const rows = q.data?.data || [];

  return (
    <div className="space-y-2">
      <p className="text-sm text-muted">
        Open opportunities tagged Parts. Set an opportunity's <em>domain</em> to <code>parts</code> to appear here.
      </p>
      <div className="bg-white border border-panelDark rounded overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-panel text-navy uppercase text-[10px] tracking-wide">
            <tr>
              <th className="text-left px-3 py-2">Name</th>
              <th className="text-left px-3 py-2">Customer</th>
              <th className="text-left px-3 py-2">Stage</th>
              <th className="text-right px-3 py-2">Value</th>
              <th className="text-right px-3 py-2">Activity</th>
              <th className="text-right px-3 py-2">Close by</th>
              <th className="text-left px-3 py-2">Owner</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(o => (
              <tr key={o.id} className="border-t border-panel hover:bg-panel/60">
                <td className="px-3 py-1.5">
                  <Link to={`/pipeline/${o.id}`} className="text-navy hover:text-crimson font-medium">{o.name}</Link>
                </td>
                <td className="px-3 py-1.5">
                  {o.customer_id
                    ? <Link to={`/customers/${o.customer_id}`} className="text-navy hover:text-crimson">{o.customer_name}</Link>
                    : '—'}
                </td>
                <td className="px-3 py-1.5 capitalize">{o.stage}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{money(o.value_usd)}</td>
                <td className="px-3 py-1.5 text-right text-xs text-muted">
                  {o.source_note_date ? new Date(o.source_note_date).toLocaleDateString() : '—'}
                </td>
                <td className="px-3 py-1.5 text-right text-xs text-muted">{o.expected_close_date || '—'}</td>
                <td className="px-3 py-1.5 text-xs text-muted">{o.owner_name || '—'}</td>
              </tr>
            ))}
            {!q.isLoading && rows.length === 0 && (
              <tr><td colSpan={7} className="text-center text-muted py-6">No parts opportunities yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div className={`card p-3 text-center${highlight ? ' ring-1 ring-crimson' : ''}`}>
      <div className={`text-lg font-bold${highlight ? ' text-crimson' : ' text-navy'}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-muted">{label}</div>
    </div>
  );
}
